import { useMemo, useState } from 'react'
import { LiveQuizMascot } from '@/features/editor/components/blocks/previews/quizMascotContext'
import { X } from 'lucide-react'
import { Button, SegmentedControl, cn } from '@teach/ui'
import type { LessonType } from '@/types/lesson'
import {
  ACTIVE_BLOCK_CARDS,
  type ActiveBlockCard,
  type BlockCardCategory,
} from '@/features/editor/constants/blockTypeMatrix'
import { blockSample } from '@/features/editor/lib/blockSamples'
import { useAppSelector } from '@/store/hooks'
import { AddBlockPickerRow } from './AddBlockPickerRow'
import { blockSampleRun } from './blockSampleRun'
import { useSlideFromCenter } from './useSlideFromCenter'
import { PhoneCanvas } from './previews/PhoneCanvas'
import { IncomingBlockPreview } from './IncomingBlockPreview'
import { LessonProgressContext } from './previews/lessonProgress'

import { renderBlockPreview } from './previews/renderBlockPreview'
import type { NewBlockPreview } from '@/features/editor/lib/newBlockPreview'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/** Полная полоса прогресса: последний блок урока из одного. */
const FULL_PROGRESS = { total: 1, current: 0 }

interface Props {
  onAdd: (card: ActiveBlockCard) => void
  onClose: () => void
  /** Вид блока, который появится на холсте, — проявляется поверх примера. */
  previewFor?: (card: ActiveBlockCard) => NewBlockPreview | null
  lessonType: LessonType
}

// Иконки групп — фирменная графика (цветная, поэтому картинкой, а не lucide).
// Размер свой, а не дефолтные 14px контрола: мелкая цветная графика читалась бы
// пятном. Карандаш нарисован с полями внутри — ему нужно больше места.
const groupIcon = (src: string, size: string) => {
  const GroupIcon = () => <img src={src} alt="" className={cn(size, 'object-contain')} />
  return GroupIcon
}

const GROUPS = [
  {
    id: 'content',
    labelKey: 'groupTheory',
    icon: groupIcon('/icons/blocks-theory.svg', 'size-5'),
  },
  {
    id: 'interactive',
    labelKey: 'groupPractice',
    icon: groupIcon('/icons/blocks-practice.svg', 'size-6'),
  },
] satisfies {
  id: BlockCardCategory
  labelKey: 'groupTheory' | 'groupPractice'
  icon: unknown
}[]

/**
 * Типов «скоро» (заголовок, текст, аудио…) здесь нет намеренно: показываем только то,
 * что можно добавить прямо сейчас.
 *
 * Выбор типа нового экрана на холсте редактора: слева типы списком по группам,
 * справа — как выглядит выбранный экран. Добавляет клик по строке (наведение показывает пример).
 */
export const AddBlockPicker = ({ onAdd, onClose, lessonType, previewFor }: Props) => {
  const m = useMessages(messages)
  const groups = GROUPS.map(({ labelKey, ...g }) => ({ ...g, label: m[labelKey] }))
  const contentLang = useAppSelector((s) => s.editor.contentLang)
  const isBlocked = (c: ActiveBlockCard) => lessonType === 'practical' && c.theoreticalOnly === true
  const available = ACTIVE_BLOCK_CARDS.filter((c) => !isBlocked(c))
  const firstIn = (g: BlockCardCategory) => available.find((c) => c.category === g) ?? null
  // Переключатель групп, как в настройках; по умолчанию — теоретические.
  const [group, setGroup] = useState<BlockCardCategory>('content')
  const [picked, setPicked] = useState<ActiveBlockCard | null>(
    () => firstIn('content') ?? firstIn('interactive'),
  )
  const switchGroup = (g: BlockCardCategory) => {
    setGroup(g)
    setPicked(firstIn(g))
  }
  const sample = useMemo(() => (picked ? blockSample(picked) : null), [picked])
  const { ref: phoneRef, slideBack } = useSlideFromCenter<HTMLDivElement>()
  // Выбрали тип (или закрыли окно) — экран едет обратно в центр, список гаснет,
  // и только потом окно уступает место новому экрану.
  const [leaving, setLeaving] = useState(false)
  // Блок, в который перетекает пример, пока телефон едет в центр.
  const [incoming, setIncoming] = useState<NewBlockPreview | null>(null)
  const leave = (then: () => void, next: NewBlockPreview | null = null) => {
    if (leaving) return
    setLeaving(true)
    setIncoming(next)
    void slideBack().then(then)
  }

  return (
    <div className="flex h-full w-full max-w-4xl gap-6" onClick={(e) => e.stopPropagation()}>
      <div
        className={cn(
          'flex w-full shrink-0 flex-col overflow-y-auto md:w-80 md:[&>:first-child]:mt-auto md:[&>:last-child]:mb-auto duration-500 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden animate-in fade-in slide-in-from-left-8',
          leaving ? 'pointer-events-none -translate-x-8 opacity-0 transition duration-300' : null,
        )}
      >
        <div className="mb-4 flex items-center justify-between px-2">
          <h2 className="text-lg font-semibold">{m.addBlock}</h2>
          {/* На телефоне колонки примера нет — крестик живёт в шапке списка. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => leave(onClose)}
            aria-label={m.close}
            className="md:hidden"
          >
            <X className="size-5" />
          </Button>
        </div>
        <SegmentedControl
          fullWidth
          className="mb-4"
          value={group}
          onChange={switchGroup}
          options={groups}
        />
        {ACTIVE_BLOCK_CARDS.filter((c) => c.category === group).map((card) => (
          <AddBlockPickerRow
            key={card.id}
            card={card}
            picked={picked?.id === card.id}
            blocked={isBlocked(card)}
            onPick={() => setPicked(card)}
            onAdd={() => leave(() => onAdd(card), previewFor?.(card) ?? null)}
          />
        ))}
      </div>

      {/* Пример блока — только с `md`: на телефоне колонка рядом со списком
          не помещалась, и телефон-пример наезжал на подписи типов. */}
      <div className="relative hidden min-w-0 flex-1 flex-col items-center gap-4 [container-type:size] md:flex">
        {picked && sample ? (
          // Вписан в колонку с обеих сторон. По одной высоте в высоком окне (мелкий масштаб)
          // телефон становился шире колонки и наезжал на список типов.
          <div
            ref={phoneRef}
            className="relative my-auto aspect-[390/760] min-h-0"
            style={{ width: 'min(100cqw, calc(100cqh * 390 / 760))' }}
          >
            <div className="h-full">
              {/* key: смена типа — новый экран, а не перетекание одного в другой
                  (иначе кружки вариантов анимированно становились квадратиками). */}
              <LiveQuizMascot>
                <PhoneCanvas key={picked.id} className="rounded-2xl">
                  {/* Полоса у примера — полная: новый блок встаёт в конец урока,
                      и при перетекании в него полоса не дёргается. */}
                  <LessonProgressContext.Provider value={FULL_PROGRESS}>
                    {renderBlockPreview(sample, contentLang, { run: blockSampleRun(picked) })}
                  </LessonProgressContext.Provider>
                </PhoneCanvas>
              </LiveQuizMascot>
            </div>
            {/* Перетекание: пока телефон едет в центр, поверх примера проявляется
                ровно тот блок, что встанет на холст, — без затемнения между ними.
                `onDesignChange` — тот же «редактируемый» вид пустого составного
                блока, что на холсте. */}
            {incoming ? (
              <IncomingBlockPreview preview={incoming} contentLang={contentLang} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
